import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  FormHelperText,
  TextField,
  Button,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enIN } from "date-fns/locale";
import StartIcon from "../../images/schedule/start.svg";
import StopIcon from "../../images/schedule/stop.svg";
import { getUserTimeFormat } from "../../utils/formatDate";

const RequiredStar = () => (
  <Typography component="span" color="error" sx={{ ml: 0.5, fontSize: "20px" }}>
    *
  </Typography>
);

const FieldLabel = ({ label, required }) => (
  <Typography
    component="span"
    sx={{
      display: "block",
      mb: 1,
      fontWeight: "medium",
      fontSize: "0.875rem",
    }}
  >
    {label}
    {required && <RequiredStar />}
  </Typography>
);

const StyledTableHead = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#B7BFCC",
  color: theme.palette.primary.contrastText,
  fontWeight: "bold",
  paddingTop: 0,
  paddingBottom: 0,
  height: "48px",
}));

const StopSchedulesTable = ({
  stopSchedules,
  errors,
  handleStopChange,
  isViewMode = false,
  onCancel,
  onSave,
}) => {
  // Convert HH:mm to Date object for TimePicker
  const parseTimeToDate = (timeStr) => {
    if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) return null;
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Convert Date object to HH:mm
  const formatDateToTime = (date) => {
    if (!date || isNaN(date)) return "";
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Format time for display (12-hour format)
  const formatTimeForDisplay = (timeStr) => {
    if (!timeStr) return "N/A";
    const [hours, minutes] = timeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return "Invalid Time";
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Helper function to get field styles
  const getReadOnlyFieldStyles = (index, fieldType) => {
    const isFirstStopArrival = index === 0 && fieldType === "arrival";
    const isLastStopDeparture =
      index === stopSchedules.length - 1 && fieldType === "departure";
    return {
      "& .MuiOutlinedInput-root": {
        height: "55px",
        borderRadius: "16px",
        backgroundColor:
          isFirstStopArrival || isLastStopDeparture ? "#E8EAEE" : "inherit",
        "& fieldset": {
          border:
            isFirstStopArrival || isLastStopDeparture
              ? "none"
              : "2px solid #183059",
        },
        "&:hover fieldset": {
          border:
            isFirstStopArrival || isLastStopDeparture
              ? "none"
              : "2px solid #183059",
        },
        "&.Mui-focused fieldset": {
          border:
            isFirstStopArrival || isLastStopDeparture
              ? "none"
              : "2px solid #183059",
        },
      },
      "& .MuiInputBase-input": {
        color: isFirstStopArrival
          ? "#008C23"
          : isLastStopDeparture
          ? "#EE3B2B"
          : "#000000",
      },
    };
  };

  // Add new function to validate time sequence
  const validateTimeSequence = (index, type, newValue) => {
    const isFirstStop = index === 0;
    const isLastStop = index === stopSchedules.length - 1;

    const newTime = formatDateToTime(newValue);
    if (!newTime) return true;

    const newMinutes = getMinutesFromTime(newTime);
    const currentStop = stopSchedules[index];
    const prevStop = index > 0 ? stopSchedules[index - 1] : null;
    const nextStop = index < stopSchedules.length - 1 ? stopSchedules[index + 1] : null;

    // Validate based on field type
    if (type === "arrivalTime") {
      // For first stop: arrival time is same as departure time, no validation needed
      if (isFirstStop) return true;

      // Arrival time must be AFTER previous stop's departure time
      if (prevStop?.departureTime) {
        const prevDeparture = getMinutesFromTime(prevStop.departureTime);
        if (newMinutes <= prevDeparture) {
          console.log(`Arrival time (${newTime}) must be after previous departure (${prevStop.departureTime})`);
          return false;
        }
      }

      // Arrival time must be BEFORE this stop's departure time
      if (currentStop.departureTime) {
        const currentDeparture = getMinutesFromTime(currentStop.departureTime);
        if (newMinutes >= currentDeparture) {
          console.log(`Arrival time (${newTime}) must be before departure time (${currentStop.departureTime})`);
          return false;
        }
      }

      return true;
    }

    if (type === "departureTime") {
      // For last stop: departure time is same as arrival time, no validation needed
      if (isLastStop) return true;

      // Departure time must be AFTER this stop's arrival time
      if (currentStop.arrivalTime) {
        const currentArrival = getMinutesFromTime(currentStop.arrivalTime);
        if (newMinutes <= currentArrival) {
          console.log(`Departure time (${newTime}) must be after arrival time (${currentStop.arrivalTime})`);
          return false;
        }
      }

      // Departure time must be BEFORE next stop's arrival time
      if (nextStop?.arrivalTime) {
        const nextArrival = getMinutesFromTime(nextStop.arrivalTime);
        if (newMinutes >= nextArrival) {
          console.log(`Departure time (${newTime}) must be before next arrival time (${nextStop.arrivalTime})`);
          return false;
        }
      }

      return true;
    }

    return true;
  };

  // Helper function to convert HH:mm to minutes
  const getMinutesFromTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Modify the handleStopChange wrapper
  const handleTimeChange = (index, type, newValue) => {
    if (!validateTimeSequence(index, type, newValue)) {
      // Show error message
      handleStopChange(index, {
        target: {
          name: type,
          value: formatDateToTime(newValue),
        },
      });
      return;
    }

    const formattedTime = formatDateToTime(newValue);

    // The parent keeps first/last stop boundary times in sync, so a single
    // update here avoids stale-state overwrites when editing boundary stops.
    if (index === 0 && type === "departureTime") {
      handleStopChange(index, {
        target: {
          name: "departureTime",
          value: formattedTime,
        },
      });
      return;
    }

    if (index === stopSchedules.length - 1 && type === "arrivalTime") {
      handleStopChange(index, {
        target: {
          name: "arrivalTime",
          value: formattedTime,
        },
      });
      return;
    }

    handleStopChange(index, {
      target: {
        name: type,
        value: formattedTime,
      },
    });
  };

  const userTimeFormat = getUserTimeFormat();
  const is24Hour = userTimeFormat === "24hr";
  const timePlaceholder = is24Hour ? "--:--" : "--:-- --";
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
      <TableContainer sx={{ mb: 4, borderRadius: "12px 12px 0 0", overflow: "hidden" }}>
        <Table sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHead
            sx={{
              backgroundColor: "#B7BFCC",
              height: "48px",
              borderRadius: "12px 12px 0 0",
              overflow: "hidden",
            }}
          >
            <TableRow
              sx={{
                backgroundColor: "#B7BFCC",
                height: "48px",
              }}
            >
              <StyledTableHead
                sx={{
                  backgroundColor: "#B7BFCC",
                  color: "#1F242E",
                  fontSize: "20px",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    backgroundColor: "#B7BFCC",
                    color: "#1F242E",
                    fontSize: "20px",
                  }}
                >
                   Stops
                </Typography>
              </StyledTableHead>
              <StyledTableHead
                sx={{
                  backgroundColor: "#B7BFCC",
                  color: "#1F242E",
                  fontSize: "20px",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    backgroundColor: "#B7BFCC",
                    color: "#1F242E",
                    fontSize: "20px",
                  }}
                >
                  Arrival Time <RequiredStar />
                </Typography>
              </StyledTableHead>
              <StyledTableHead
                sx={{
                  backgroundColor: "#B7BFCC",
                  color: "#1F242E",
                  fontSize: "20px",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    backgroundColor: "#B7BFCC",
                    color: "#1F242E",
                    fontSize: "20px",
                  }}
                >
                  Departure Time <RequiredStar />
                </Typography>
              </StyledTableHead>
            </TableRow>
          </TableHead>
          <TableBody>
            {stopSchedules.map((stop, index) => {
              const isFirstStop = index === 0;
              const isLastStop = index === stopSchedules.length - 1;

              return (
                <TableRow key={index}>
                  <TableCell>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "20px",
                        color: "#111214",
                      }}
                    >
                      {stop.stopName}
                    </Typography>
                  </TableCell>

                  {/* Arrival Time Cell - Hidden for first stop */}
                  <TableCell>
                    {isFirstStop ? (
                      <Box sx={{ color: "#999", fontStyle: "italic" }}>-</Box>
                    ) : isViewMode ? (
                      <Box display="flex" alignItems="center">
                        <TextField
                          value={formatTimeForDisplay(stop.arrivalTime)}
                          fullWidth
                          size="small"
                          sx={getReadOnlyFieldStyles(index, "arrival")}
                          InputProps={{
                            readOnly: true,
                            endAdornment: isLastStop ? (
                              <Box
                                sx={{
                                  bgcolor: "#EE3B2B",
                                  color: "#FFFFFF",
                                  gap: "6px",
                                  borderRadius: "6px",
                                  padding: "2px 6px",
                                  display: "flex",
                                  alignItems: "center",
                                  ml: 1,
                                }}
                              >
                                <img
                                  src={StopIcon}
                                  alt="start"
                                  style={{ width: 12, height: 12 }}
                                />
                                <span style={{ fontSize: "12px" }}>Stop</span>
                              </Box>
                            ) : null,
                          }}
                        />
                      </Box>
                    ) : (
                      <>
                        <TimePicker
                          value={parseTimeToDate(stop.arrivalTime)}
                          format={is24Hour ? "HH:mm" : "hh:mm a"}
                          onChange={(newValue) =>
                            handleTimeChange(index, "arrivalTime", newValue)
                          }
                          disabled={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: !isFirstStop,
                              size: "small",
                              placeholder: timePlaceholder,
                              error: !!errors[`stop-${index}-arrivalTime`],
                              sx: getReadOnlyFieldStyles(index, "arrival"),
                              InputProps: isLastStop
                                ? {
                                    endAdornment: (
                                      <Box
                                        sx={{
                                          bgcolor: "#EE3B2B",
                                          color: "#FFFFFF",
                                          gap: "8px",
                                          borderRadius: "6px",
                                          padding: "0px 5px",
                                          display: "flex",
                                          justifyItems: "center",
                                        }}
                                      >
                                        <img src={StopIcon} alt="start" />
                                        Stop
                                      </Box>
                                    ),
                                  }
                                : {},
                            },
                            openPickerIcon: { sx: { display: "none" } },
                          }}
                          ampm={!is24Hour}
                        />

                        {errors[`stop-${index}-arrivalTime`] && (
                          <FormHelperText error>
                            {errors[`stop-${index}-arrivalTime`]}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  </TableCell>

                  {/* Departure Time Cell - Hidden for last stop */}
                  <TableCell>
                    {isLastStop ? (
                      <Box sx={{ color: "#999", fontStyle: "italic" }}>-</Box>
                    ) : isViewMode ? (
                      <Box display="flex" alignItems="center">
                        <TextField
                          value={formatTimeForDisplay(stop.departureTime)}
                          fullWidth
                          size="small"
                          sx={getReadOnlyFieldStyles(index, "departure")}
                          InputProps={{
                            readOnly: true,
                            endAdornment: isFirstStop ? (
                              <Box
                                sx={{
                                  bgcolor: "#008C23",
                                  color: "#FFFFFF",
                                  gap: "6px",
                                  borderRadius: "6px",
                                  padding: "2px 6px",
                                  display: "flex",
                                  alignItems: "center",
                                  ml: 1,
                                  height: "39px",
                                }}
                              >
                                <img
                                  src={StartIcon}
                                  alt="start"
                                  style={{ width: 12, height: 12 }}
                                />
                                <span style={{ fontSize: "12px" }}>Start</span>
                              </Box>
                            ) : null,
                          }}
                        />
                      </Box>
                    ) : (
                      <>
                        <TimePicker
                          value={parseTimeToDate(stop.departureTime)}
                          format={is24Hour ? "HH:mm" : "hh:mm a"}
                          onChange={(newValue) =>
                            handleTimeChange(index, "departureTime", newValue)
                          }
                          disabled={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: !isLastStop,
                              size: "small",
                              error: !!errors[`stop-${index}-departureTime`],
                              placeholder: timePlaceholder,
                              sx: getReadOnlyFieldStyles(index, "departure"),
                              InputProps: isFirstStop
                                ? {
                                    endAdornment: (
                                      <Box
                                        sx={{
                                          bgcolor: "#008C23",
                                          color: "#FFFFFF",
                                          gap: "8px",
                                          borderRadius: "6px",
                                          padding: "0px 5px",
                                          display: "flex",
                                          justifyItems: "center",
                                        }}
                                      >
                                        <img src={StartIcon} alt="start" />
                                        Start
                                      </Box>
                                    ),
                                  }
                                : {},
                            },
                            openPickerIcon: { sx: { display: "none" } },
                          }}
                          ampm={!is24Hour}
                        />
                        {errors[`stop-${index}-departureTime`] && (
                          <FormHelperText error>
                            {errors[`stop-${index}-departureTime`]}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </LocalizationProvider>
  );
};

export default StopSchedulesTable;
